import React from "react";
import Modal from "react-modal";
import { Field, Form, Formik } from "formik";
import { Oval } from "react-loader-spinner";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../../../app/hooks";
import { setEditingKey, updateApiKey } from "../../../../apiKeysSlice";
import { ApiKeyRole } from "../../../../../../models";

import "./EditApiKeyModal.scss";

const modalStyles = {
  content: {
    top: "50%", left: "50%", right: "auto", bottom: "auto",
    marginRight: "-50%", transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required"),
  role: Yup.mixed<ApiKeyRole>()
    .oneOf([ApiKeyRole.Read, ApiKeyRole.Write])
    .required("Role is required"),
});

export function EditApiKeyModal() {
  const dispatch = useAppDispatch();
  const editingKey = useAppSelector((state) => state.apiKeys.editingKey);
  const updateAPI = useAppSelector((state) => state.apiKeys.updateApiKey);

  React.useEffect(() => {
    if (!updateAPI.loading && !!updateAPI.error) {
      toast.error("Couldn't update the API key. Please try again.");
    }
  }, [updateAPI.loading, updateAPI.error]);

  const onClose = () => dispatch(setEditingKey(undefined));

  const onSubmit = (form: { name: string; role: ApiKeyRole }) => {
    if (!editingKey) return;
    dispatch(updateApiKey({ id: editingKey.id, req: { name: form.name.trim(), role: form.role } }));
  };

  return (
    <Modal isOpen={!!editingKey} onRequestClose={onClose} style={modalStyles}>
      <div className="edit-api-key-modal-component">
        <div className="title">Edit API Key</div>
        {editingKey && (
          <Formik
            initialValues={{ name: editingKey.name, role: editingKey.role }}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {({ errors, touched }) => (
              <Form className="edit-api-key-form">
                <div className="field-container">
                  <div className="label">Name:</div>
                  <Field id="name" name="name" className="text-input" />
                  {errors.name && touched.name && (
                    <div className="form-error">{errors.name}</div>
                  )}
                </div>

                <div className="field-container">
                  <div className="label">Role:</div>
                  <Field as="select" id="role" name="role" className="text-input">
                    <option value={ApiKeyRole.Read}>Read</option>
                    <option value={ApiKeyRole.Write}>Write</option>
                  </Field>
                </div>

                <button type="submit" className="submit-button" disabled={updateAPI.loading}>
                  Save
                  {updateAPI.loading && (
                    <div style={{ marginLeft: 8 }}>
                      <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A" />
                    </div>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
}
